// Cooling-stage SLA thresholds in hours [warn, crit] — shared by the Precool
// board's live age chips and the cold-chain register's dwell tinting, so the
// paper and the floor never disagree about what "too long" means.
//
// TUNABLE DEFAULTS pending operator numbers (audit FC-17 / berry-gap G-16):
// berries want cooling to START within ~2h of intake; holding bands mirror
// FLOW_AGE_POLICY.BLUEBERRY (warn 12h / crit 18h before the 24h export window
// burns). FCX-22 moves these to per-tenant config — keep this the single
// in-code source until then.

export type CoolingStage =
  'STAGING' | 'COOLING' | 'IN_HOLDING' | 'RELEASED' | 'TIPPED' | 'CANCELLED'

export const STAGE_SLA_H: Record<CoolingStage, [number, number]> = {
  STAGING:    [2, 4],
  COOLING:    [4, 8],
  IN_HOLDING: [12, 18],
  RELEASED:   [12, 18],
  TIPPED:     [Infinity, Infinity],
  CANCELLED:  [Infinity, Infinity],
}

// The row shape every stage-clock consumer carries (board cards, the floor
// monitor strip) — just the stamps the anchor needs.
export interface StageClockRow {
  status: CoolingStage
  created_at: string
  cool_started_at: string | null
  cool_ended_at: string | null
  released_at: string | null
}

// When a batch ENTERED its current stage — the clock the SLA bands run on.
// Flat status→timestamp switch.
// fallow-ignore-next-line complexity
export function stageAnchor(b: StageClockRow): string {
  switch (b.status) {
    case 'COOLING':    return b.cool_started_at ?? b.created_at
    case 'IN_HOLDING': return b.cool_ended_at   ?? b.created_at
    case 'RELEASED':   return b.released_at     ?? b.created_at
    default:           return b.created_at
  }
}

// Is this batch past its stage's CRIT threshold right now? `sla` defaults to
// the in-code record; hook consumers pass the tenant's merged config (FCX-22).
export function isOverSla(b: StageClockRow, nowMs: number, sla: Record<CoolingStage, [number, number]> = STAGE_SLA_H): boolean {
  const hrs = (nowMs - new Date(stageAnchor(b)).getTime()) / 3_600_000
  return hrs >= sla[b.status][1]
}

// Hours between two stamps (open end = null → null, not 0).
// Flat guard chain — one early return per invalid input.
// fallow-ignore-next-line complexity
export function hoursBetween(startIso: string | null, endIso: string | null): number | null {
  if (!startIso || !endIso) return null
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  return ms / 3_600_000
}

// Print-safe tint for a dwell against a stage's SLA — text colour only (print
// engines keep text colour; backgrounds get stripped). `sla` as in isOverSla.
export function dwellTintClass(hours: number | null, stage: CoolingStage, sla: Record<CoolingStage, [number, number]> = STAGE_SLA_H): string {
  if (hours == null) return ''
  const [warnH, critH] = sla[stage]
  if (hours >= critH) return 'text-red-700 font-semibold'
  if (hours >= warnH) return 'text-amber-700 font-semibold'
  return ''
}
